import { useEffect, useState } from "react";
import PolicyDialog from "../policy-dialog/policy-dialog";
import "./purchase-sheet-style.scss";

//—— 購買資訊 ——
//
//  從下方升起的 sheet：選方案 → 看明細 → 結帳。
//  版型沿用站內既有的 sheet（見 dialog/invoice-sheet），
//  色系用薄荷／青藍，跟白夜故事頁一致。
//
//  方案由外面傳進來，不寫死在這裡 —— 價格與組合是business的事，
//  之後要改成從後端拿（跟 order 建單用的必須是同一份），
//  那時只要換掉呼叫端傳的 plans，這支元件不用動。
//
//  props:
//    open      是否顯示
//    plans     [{ id, title, note, tag, original, price }]，price 是實付
//    onClose   關閉
//    onSubmit  帶著選中的方案繼續結帳
//    submitting 結帳處理中（鎖住按鈕）
//  theme：挑一組重點色（見 scss 的 --ps-accent 那段）。
//  不給就是白夜那組薄荷綠 —— 舊的呼叫端不用改。
//  值對應 constant/products.js 的 theme 欄位（鏡月是 "violet"）。
const PurchaseSheet = ({
  open,
  plans = [],
  onClose,
  onSubmit,
  submitting,
  theme,
}) => {
  const [picked, setPicked] = useState(plans[0]?.id);
  const [consent, setConsent] = useState(true);
  //目前攤開的是哪一份條文（privacy / terms / marketing），沒有就是 null。
  //  三份共用一個狀態而不是三個 boolean：它們互斥，
  //  分開存就有可能兩份同時為 true，疊出兩個視窗。
  const [doc, setDoc] = useState(null);

  //方案換了（例如之後改成從後端拿）就把選取重設到第一個，
  //  不然會停在一個已經不存在的 id 上，畫面看起來一個都沒選。
  useEffect(() => {
    setPicked((prev) =>
      plans.some((p) => p.id === prev) ? prev : plans[0]?.id,
    );
  }, [plans]);

  //sheet 打開時鎖住背景捲動，不然手指在 sheet 外面滑會捲到故事頁
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!open) return null;

  const plan = plans.find((p) => p.id === picked) || plans[0];
  if (!plan) return null;

  const saved = plan.original - plan.price;
  //折數用「實付 ÷ 原價」算，不另外存一個欄位 ——
  //  存兩份數字的話，改價時一定有人只改一邊。
  //
  //  整十的折數寫成一位數（70 → 7 折）：中文的習慣是「七折」不是「七十折」，
  //  只有不整十時才寫兩位（76 折）。
  const toOff = (p) => {
    const n = Math.round((p.price / p.original) * 100);
    return n % 10 === 0 ? n / 10 : n;
  };
  const off = toOff(plan);
  const money = (n) => `NT$${n.toLocaleString("en-US")}`;

  return (
    <div
      className={`ps${theme ? ` ps--${theme}` : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="購買資訊"
    >
      {/* 遮罩本身可點關閉 —— 手機上這是最順手的關法 */}
      <div className="ps__mask" onClick={onClose} />

      <div className="ps__panel">
        <div className="ps__head">
          <p className="ps__price-line">
            <span className="ps__off">限時{off}折</span>
            {money(plan.price)}
          </p>
          <button
            type="button"
            className="ps__close"
            onClick={onClose}
            aria-label="關閉"
          >
            ✕
          </button>
        </div>

        {saved > 0 && (
          <p className="ps__saving">
            共省下 <b>{money(saved)}</b>！
          </p>
        )}

        <p className="ps__section">限時優惠</p>

        {/* role=radiogroup：這幾張卡是互斥的選項，不是三顆獨立按鈕。
            少了它，讀屏會把它們唸成三個不相干的按鈕。 */}
        <div role="radiogroup" aria-label="限時優惠">
          {plans.map((p) => {
            const on = p.id === picked;
            return (
              <button
                type="button"
                key={p.id}
                role="radio"
                aria-checked={on}
                className={`ps__plan${on ? " is-on" : ""}`}
                onClick={() => setPicked(p.id)}
              >
                <span className="ps__radio" aria-hidden="true" />

                <span className="ps__plan-main">
                  <span className="ps__plan-title">{p.title}</span>
                  <span className="ps__plan-note">
                    {p.tag && <span className="ps__tag">{p.tag}</span>}
                    {p.note}
                  </span>
                </span>

                <span className="ps__plan-price">
                  <span className="ps__plan-off">
                    {toOff(p)}折
                    <s>{money(p.original)}</s>
                  </span>
                  <span className="ps__plan-now">{money(p.price)}</span>
                </span>
              </button>
            );
          })}
        </div>

        <div className="ps__rows">
          <div className="ps__row">
            <span>{plan.title} 原價</span>
            <span>{money(plan.original)}</span>
          </div>
          {saved > 0 && (
            <div className="ps__row is-off">
              <span>立即付款折扣</span>
              <span>-{money(saved)}</span>
            </div>
          )}
        </div>

        <div className="ps__coupon">
          <button type="button" className="ps__coupon-btn">
            套用優惠券
          </button>
          <span className="ps__coupon-value">無</span>
        </div>

        <button
          type="button"
          className="ps__submit"
          disabled={submitting}
          onClick={() => onSubmit?.({ plan, consent })}
        >
          {money(plan.price)} 繼續
        </button>

        <p className="ps__terms">
          已確認付款資訊，並同意{" "}
          <button
            type="button"
            className="ps__link"
            onClick={() => setDoc("privacy")}
          >
            隱私權政策
          </button>
          、{" "}
          <button
            type="button"
            className="ps__link"
            onClick={() => setDoc("terms")}
          >
            使用條款
          </button>
          。
        </p>

        <div className="ps__consent">
          <button
            type="button"
            className={`ps__check${consent ? " is-on" : ""}`}
            aria-pressed={consent}
            onClick={() => setConsent((v) => !v)}
          >
            <span className="ps__box" aria-hidden="true">
              ✓
            </span>
            同意接收行銷資訊（選填）
          </button>
          <button
            type="button"
            className="ps__detail"
            onClick={() => setDoc("marketing")}
          >
            詳細資訊
          </button>
        </div>
      </div>

      {/* 疊在 sheet 之上。放在 .ps 裡面而不是 fragment 平行擺，
          是為了讓它跟著 sheet 一起被卸載 —— sheet 關掉時說明視窗不該還留著。 */}
      <PolicyDialog open={!!doc} doc={doc} onClose={() => setDoc(null)} />
    </div>
  );
};

export default PurchaseSheet;
